import { Link } from 'react-router-dom';
import { Shield, Users, Heart, CheckCircle, Eye, FileCheck } from 'lucide-react';

export function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Acerca de DonaApp</h1>
      <p className="text-lg text-gray-600 mb-10">
        Conectamos personas que quieren ayudar con causas que necesitan productos reales.
      </p>

      {/* Misión */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
          <Heart className="w-5 h-5 text-indigo-600 mr-2" />
          Nuestra Misión
        </h2>
        <p className="text-gray-700 leading-relaxed">
          DonaApp nació con una premisa simple: muchas personas quieren ayudar pero no saben cómo, y muchas 
          causas necesitan productos específicos pero no tienen cómo conectar con donantes. Nuestra misión es 
          eliminar esa brecha mediante tecnología accesible, transparente y verificable.
        </p>
        <p className="text-gray-700 leading-relaxed mt-3">
          A diferencia de plataformas de donación monetaria, DonaApp se enfoca en donaciones en especie: 
          alimentos, ropa, materiales de construcción, útiles escolares, medicamentos y cualquier producto 
          tangible que una comunidad o persona necesite.
        </p>
      </section>

      {/* Quiénes somos */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
          <Users className="w-5 h-5 text-indigo-600 mr-2" />
          Quiénes Somos
        </h2>
        <p className="text-gray-700 leading-relaxed">
          DonaApp es operada por <strong>OVIEDOTECH S.A.S.</strong> (NIT 901.964.524-2), una empresa de 
          tecnología con sede en Cali, Colombia. Nuestro equipo combina experiencia en desarrollo de software, 
          diseño de producto y trabajo con comunidades para construir herramientas que generen impacto social real.
        </p>
        <p className="text-gray-700 leading-relaxed mt-3">
          Aunque nacimos en Colombia, DonaApp está diseñada para funcionar en cualquier país y para cualquier 
          tipo de causa solidaria, familiar o comunitaria.
        </p>
      </section>

      {/* Cómo verificamos */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 text-indigo-600 mr-2" />
          Cómo Verificamos las Campañas
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          La confianza es fundamental en una plataforma de donaciones. Por eso contamos con un proceso de 
          verificación para campañas que solicitan visibilidad pública:
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
              <Eye className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">1. Revisión Manual</h3>
            <p className="text-xs text-gray-600">
              Nuestro equipo revisa la información de la campaña, el perfil del organizador y la coherencia 
              de los productos solicitados.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
              <FileCheck className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">2. Validación de Contacto</h3>
            <p className="text-xs text-gray-600">
              Verificamos que el organizador tenga datos de contacto reales y accesibles para los donantes.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mb-3">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">3. Badge de Verificación</h3>
            <p className="text-xs text-gray-600">
              Las campañas aprobadas reciben un badge visible "✓ Verificada" que los donantes pueden 
              identificar fácilmente.
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Las campañas privadas (accesibles solo por enlace directo) no requieren verificación, ya que 
          se comparten directamente entre personas de confianza.
        </p>
      </section>

      {/* Valores */}
      <section className="mb-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Nuestros Valores</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Transparencia</p>
              <p className="text-xs text-gray-600">Cada compromiso es visible. Los organizadores y donantes saben en todo momento el estado de la campaña.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Accesibilidad</p>
              <p className="text-xs text-gray-600">Donar no requiere registro. Crear una campaña es gratuito. Sin barreras innecesarias.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Privacidad</p>
              <p className="text-xs text-gray-600">Los datos del donante solo son visibles para el organizador. Opción de donación anónima siempre disponible.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 text-sm">Verificación</p>
              <p className="text-xs text-gray-600">Las campañas públicas pasan por un proceso de revisión antes de ser visibles en el marketplace.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">¿Tienes preguntas?</h2>
        <p className="text-sm text-gray-600 mb-3">
          Escríbenos a{' '}
          <a href="mailto:contacto@donaapp.co" className="text-indigo-600 hover:underline">contacto@donaapp.co</a>{' '}
          o revisa nuestras{' '}
          <Link to="/faq" className="text-indigo-600 hover:underline">Preguntas Frecuentes</Link>.
        </p>
      </section>

      <div className="mt-10 pt-6 border-t border-gray-200">
        <Link to="/" className="text-sm text-indigo-600 hover:underline">← Volver al inicio</Link>
      </div>
    </div>
  );
}
